import { MouseEvent, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { ScrollSync, ScrollSyncPane } from "react-scroll-sync";
import styled, { css, useTheme } from "styled-components";
import useChart from "../hooks/useChart";
import { useIntersectionObserver, useSticky } from "../hooks/useSticky";
import { toggleShowOnlyDiffs } from "../store/chart";
import { FontWeight } from "../types";
import { Container } from "../components/Container";
import {
  ProductBestFor,
  ProductBody,
  ProductFooter,
  ProductHeader,
} from "../components/Product";
import {
  DesktopTable,
  DesktopTableColumn,
  DesktopTableHeader,
  DesktopTableHeaderRef,
  DesktopTableRow,
  DesktopTableStickyColumn,
} from "../components/Table";
import { Text } from "../components/Text";
import { Toggle } from "../components/Toggle";
import { DesktopFeatureSection } from "../components/DesktopFeatureSection";
import { trackTableVisible } from "../analytics";
import { usePreview } from "../hooks/usePreview";
import { DesktopShopSelectSection } from "../components/DesktopShopSelectSection";

type DesktopProductTableProps = {
  className?: string;
};

export function DesktopProductTable({ className }: DesktopProductTableProps) {
  const { isMobile, getValue } = useTheme();
  const dispatch = useDispatch();
  const { isPreview } = usePreview();
  const desktopTableRef = useRef<HTMLTableElement | null>(null);
  const desktopTableHeaderRef = useRef<HTMLTableSectionElement | null>(null);
  const desktopTableEntry = useIntersectionObserver(desktopTableRef, {
    rootMargin: `-${getValue(242)}px`,
    threshold: [0, 1],
  });
  const desktopSticky = useSticky(desktopTableHeaderRef, {
    threshold: [0, 1],
  });
  const {
    data: {
      styling,
      backgroundColor,
      showOnlyDiffsToggle,
      showOnlyDiffs,
      showProductApplication,
      productApplicationTitle,
      showBestFor,
      bestForTitle,
      bestForIconColor,
      bestForHoverIconColor,
      showShopButton,
      showShopSelectSection,
      shopSelectTitle,
      shopSelectDescription,
      shopSelectOpenOnLoad,
      showPrice,
      desktop: { selectedProducts, selectedFeatureMap, legalCopyArray },
      products,
      sections,
      legalCopy,
    },
  } = useChart();
  const selectedProductColumns = useMemo(
    () =>
      products.filter((product) =>
        selectedProducts.find(
          (selectedProduct) =>
            selectedProduct.id === product.id && selectedProduct.selected,
        ),
      ),
    [selectedProducts, products],
  );

  const {
    shouldShowShopButton,
    shouldShowPrice,
    shouldShowTag,
    shouldShowSalesMessage,
    shouldShowShopSelectSection,
  } = useMemo(() => {
    const shouldShowShopButton =
      showShopButton &&
      selectedProductColumns.filter((product) => product.shopUrl).length > 0;
    const shouldShowPrice =
      showPrice &&
      selectedProductColumns.filter(
        (product) => product.price || product.savePrice,
      ).length > 0;
    const shouldShowTag =
      selectedProductColumns.filter(
        (product) => product.useTag && product.tag?.text,
      ).length > 0;
    const shouldShowSalesMessage =
      selectedProductColumns.filter(
        (product) => product?.useSalesMessage && product?.salesMessage?.value,
      ).length > 0;
    const shouldShowShopSelectSection =
      showShopSelectSection &&
      selectedProductColumns.filter(
        (product) => product.showShopSelectButton && product.shopSelectButton,
      ).length > 0;
    return {
      shouldShowShopButton,
      shouldShowPrice,
      shouldShowTag,
      shouldShowSalesMessage,
      shouldShowShopSelectSection,
    };
  }, [
    selectedProductColumns,
    showPrice,
    showShopButton,
    showShopSelectSection,
  ]);

  useEffect(() => {
    trackTableVisible(products);
  }, [products]);

  function handleToggleShowOnlyDiffs() {
    dispatch(toggleShowOnlyDiffs({ isMobile }));
  }

  return (
    <DesktopTableWrapper className={className}>
      <ScrollSync>
        <div>
          <DesktopTableHeaderSticky
            active={
              !isPreview && desktopSticky && desktopTableEntry?.isIntersecting
            }
            backgroundColor={backgroundColor?.hex}
          >
            <DesktopTableDraggable>
              <DesktopTable cellSpacing="0" cellPadding="0">
                <DesktopTableHeader>
                  <DesktopTableRow>
                    <DesktopToggleColumn backgroundColor={backgroundColor?.hex}>
                      <Toggle
                        id="show-only-diffs"
                        toggle={showOnlyDiffsToggle}
                        checked={showOnlyDiffs}
                        onChange={handleToggleShowOnlyDiffs}
                      />
                    </DesktopToggleColumn>
                    {selectedProductColumns.map((product) => (
                      <DesktopTableColumn key={product.id}>
                        <ProductHeader
                          product={product}
                          styling={styling}
                          hiddenTag={!shouldShowTag}
                        />
                      </DesktopTableColumn>
                    ))}
                  </DesktopTableRow>
                </DesktopTableHeader>
              </DesktopTable>
            </DesktopTableDraggable>
          </DesktopTableHeaderSticky>

          <DesktopTableDraggable showScrollBar>
            <DesktopTable cellSpacing="0" cellPadding="0" ref={desktopTableRef}>
              <DesktopTableHeaderRef ref={desktopTableHeaderRef} />
              {selectedProductColumns.length > 0 && (
                <>
                  <DesktopTableHeader>
                    <DesktopTableRow>
                      <DesktopToggleColumn
                        backgroundColor={backgroundColor?.hex}
                      >
                        <Toggle
                          id="show-only-diffs"
                          toggle={showOnlyDiffsToggle}
                          checked={showOnlyDiffs}
                          onChange={handleToggleShowOnlyDiffs}
                        />
                      </DesktopToggleColumn>
                      {selectedProductColumns.map((product) => (
                        <DesktopTableColumn key={product.id}>
                          <ProductHeader
                            product={product}
                            styling={styling}
                            hiddenTag={!shouldShowTag}
                          />
                        </DesktopTableColumn>
                      ))}
                    </DesktopTableRow>
                    {(shouldShowPrice || shouldShowShopButton) && (
                      <DesktopTableRow>
                        <DesktopTableStickyColumn
                          backgroundColor={backgroundColor?.hex}
                        />
                        {selectedProductColumns.map((product) => (
                          <DesktopTableColumn key={product.id}>
                            <ProductBody
                              product={product}
                              styling={styling}
                              hiddenPrice={!shouldShowPrice}
                              hiddenShopButton={!shouldShowShopButton}
                              hiddenSalesMessage={!shouldShowSalesMessage}
                            />
                          </DesktopTableColumn>
                        ))}
                      </DesktopTableRow>
                    )}
                    {showProductApplication && (
                      <DesktopTableRow>
                        <DesktopTableStickyColumn
                          backgroundColor={backgroundColor?.hex}
                        >
                          <DesktopApplicationColumn>
                            <DesktopApplicationTitle
                              font={productApplicationTitle?.font}
                              textColor={productApplicationTitle?.color}
                            >
                              {productApplicationTitle?.value}
                            </DesktopApplicationTitle>
                          </DesktopApplicationColumn>
                        </DesktopTableStickyColumn>
                        {selectedProductColumns.map((product) => (
                          <DesktopTableColumn key={product.id}>
                            <ProductFooter
                              product={product}
                              styling={styling}
                            />
                          </DesktopTableColumn>
                        ))}
                      </DesktopTableRow>
                    )}
                    {showBestFor && (
                      <DesktopTableRow>
                        <DesktopTableStickyColumn
                          backgroundColor={backgroundColor?.hex}
                        >
                          <DesktopApplicationColumn>
                            <DesktopApplicationTitle
                              font={bestForTitle?.font}
                              textColor={bestForTitle?.color}
                            >
                              {bestForTitle?.value}
                            </DesktopApplicationTitle>
                          </DesktopApplicationColumn>
                        </DesktopTableStickyColumn>
                        {selectedProductColumns.map((product) => (
                          <DesktopTableColumn key={product.id}>
                            <ProductBestFor
                              product={product}
                              styling={styling}
                              iconColor={bestForIconColor}
                              hoverIconColor={bestForHoverIconColor}
                            />
                          </DesktopTableColumn>
                        ))}
                      </DesktopTableRow>
                    )}
                  </DesktopTableHeader>
                </>
              )}
              {sections.map((section) => (
                <DesktopFeatureSection
                  key={section.id}
                  section={section}
                  selectedProducts={selectedProductColumns}
                  selectedFeatureMap={selectedFeatureMap}
                  showOnlyDiffs={showOnlyDiffs}
                  legalCopyArray={legalCopyArray}
                  styling={styling}
                  backgroundColor={backgroundColor}
                />
              ))}
              {shouldShowShopSelectSection &&
              shopSelectTitle &&
              shopSelectDescription ? (
                <DesktopShopSelectSection
                  selectedProducts={selectedProductColumns}
                  styling={styling}
                  backgroundColor={backgroundColor}
                  title={shopSelectTitle}
                  description={shopSelectDescription}
                  openOnLoad={shopSelectOpenOnLoad}
                />
              ) : null}
            </DesktopTable>
          </DesktopTableDraggable>
        </div>
      </ScrollSync>
      <Container>
        <div>
          <DesktopLegalCopyList>
            {legalCopyArray.map((legalCopy, index) => (
              <li key={legalCopy}>
                <DesktopLegalCopy>
                  <sup>{index + 1}</sup>
                  {legalCopy}
                </DesktopLegalCopy>
              </li>
            ))}
          </DesktopLegalCopyList>
          {legalCopy && (
            <DesktopLegalCopyList>
              <li>
                <DesktopLegalCopy>{legalCopy}</DesktopLegalCopy>
              </li>
            </DesktopLegalCopyList>
          )}
        </div>
      </Container>
    </DesktopTableWrapper>
  );
}

type DesktopTableDraggableProps = {
  showScrollBar?: boolean;
  children: React.ReactNode;
};

function DesktopTableDraggable({
  children,
  showScrollBar,
}: DesktopTableDraggableProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const positionRef = useRef({
    scrollTop: 0,
    scrollLeft: 0,
    mouseX: 0,
    mouseY: 0,
  });

  const mouseMoveHandler = (event: { clientX: number; clientY: number }) => {
    if (ref.current) {
      // Calculate differences to see how far the user has moved
      const dx = event.clientX - positionRef.current.mouseX;
      // Scroll the element according to those differences
      ref.current.scrollLeft = positionRef.current.scrollLeft - dx;
    }
  };

  const mouseUpHandler = () => {
    // Return to cursor: grab after the user is no longer pressing
    if (ref.current) ref.current.style.cursor = "grab";

    // Remove the event listeners since it is not necessary to track the mouse position anymore
    document.removeEventListener("mousemove", mouseMoveHandler);
    document.removeEventListener("mouseup", mouseUpHandler);
    ref.current = null;
  };

  const onMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    ref.current = event.currentTarget;
    if (ref.current) {
      // Save the position at the moment the user presses down
      positionRef.current = {
        scrollLeft: ref.current.scrollLeft,
        scrollTop: ref.current.scrollTop,
        mouseX: event.clientX,
        mouseY: event.clientY,
      };

      // Show a cursor: grabbing style and set user-select: none to avoid highlighting text while dragging
      ref.current.style.cursor = "grabbing";
      ref.current.style.userSelect = "none";

      // Add the event listeners that will track the mouse position for the rest of the interaction
      document.addEventListener("mousemove", mouseMoveHandler);
      document.addEventListener("mouseup", mouseUpHandler);
    }
  };
  return (
    <DesktopTableContainer>
      <ScrollSyncPane>
        <DesktopTableOverflow
          showScrollBar={showScrollBar}
          onMouseDown={onMouseDown}
        >
          {children}
        </DesktopTableOverflow>
      </ScrollSyncPane>
    </DesktopTableContainer>
  );
}

const DesktopTableWrapper = styled.div`
  padding: ${({ theme }) =>
    `${theme.getValue(48)}px 0 ${theme.getValue(40)}px`};
`;

const DesktopTableOverflow = styled.div<{ showScrollBar?: boolean }>`
  overflow-x: auto;
  overflow-y: hidden;

  ${({ showScrollBar }) =>
    !showScrollBar
      ? css`
          ::-webkit-scrollbar {
            display: none;
          }
          -ms-overflow-style: none;
          scrollbar-width: none;
        `
      : ""};
`;

const DesktopTableContainer = styled(Container)`
  padding: ${({ theme }) =>
    `0 ${theme.getValue(100)}px 0 ${theme.getValue(56)}px`};
`;

const DesktopTableHeaderSticky = styled.div<{
  active?: boolean;
  backgroundColor?: string;
}>`
  position: fixed;
  z-index: ${({ active }) => (active ? 20 : -1)};
  top: 0;
  left: 0;
  width: 100%;
  transition: opacity 0.2s;
  opacity: ${({ active }) => (active ? 1 : 0)};
  background: ${({ theme, backgroundColor }) =>
    backgroundColor || theme.colors.green4};
  box-shadow: 0px 2px 12px rgba(0, 0, 0, 0.3);
`;

const DesktopToggleColumn = styled(DesktopTableStickyColumn)`
  display: flex;
  justify-content: flex-center;
  align-items: center;
  padding-left: ${({ theme }) => theme.getValue(44)}px;
`;

const DesktopApplicationColumn = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
  padding-left: ${({ theme }) => theme.getValue(44)}px;
`;

const DesktopApplicationTitle = styled(Text)`
  &&& {
    font-weight: ${({ font: { weight = FontWeight.SemiBold } = {} }) => weight};
    font-size: ${({ theme }) => theme.getFontSize(22)}px;
    line-height: 1;
    margin: 0;
  }
`;

const DesktopLegalCopyList = styled.ul`
  margin: ${({ theme }) => `${theme.getValue(40)}px 0`};
  padding: 0;
  list-style: none;

  li + li {
    margin-top: ${({ theme }) => `${theme.getValue(16)}px`};
  }
`;

const DesktopLegalCopy = styled(Text)`
  &&& {
    font-size: ${({ theme }) => theme.getFontSize(16)}px;
    line-height: 1.25;
  }
`;
